import clsx from 'clsx';
import { createElement, CSSProperties, forwardRef, HTMLProps, PropsWithChildren } from 'react';
import classes from './stack.module.scss';

export type StackProps = PropsWithChildren &
  {
    /**
     * Direction of the stack
     */
    direction?: 'row' | 'column';

    /**
     * Customize the spacing. Value is multiplied by 8px
     */
    spacing?: number;

    /**
     * Fills the width to 100%
     */
    fill?: boolean;

    /**
     * Specifies whether the flexible items should wrap or not
     */
    wrapped?: boolean;

    /**
     * Align items on the main axis
     */
    align?: 'start' | 'end' | 'center' | 'spread' | 'stretch';

    /**
     * Align items on the cross axis
     */
    crossAxis?: 'start' | 'end' | 'center' | 'stretch' | 'base';

    /**
     * Specifies an optional string representing the name of an intrinsic JSX element to render.
     * This allows overriding the default component or element type used in rendering.
     * The property must be a key from the `JSX.IntrinsicElements` interface.
     */
    as?: string | keyof JSX.IntrinsicElements;
  } & Omit<HTMLProps<HTMLDivElement>, 'label' | 'as'>;

/**
 * Component to manage spacing within a flex context
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      children,
      spacing = 1,
      direction = 'row',
      className,
      fill,
      wrapped,
      align,
      as = 'div',
      crossAxis,
      ...props
    },
    ref
  ) => {

    return createElement(as, {
      ...props,
      ref,
      style: {
        ...props.style,
        '--stack--factor': spacing
      } as CSSProperties,
      className: clsx(
        classes.root,
        className,
        classes[`cross_${crossAxis}`],
        classes[`align_${align}`],
        classes[direction],
        'stack-component',
        {
          [classes.fill]: fill,
          [classes.wrap]: wrapped
        }
      )
    }, children);
  }
);
